/** @jsxImportSource @emotion/react */
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useCallback, useRef } from 'react';
import UDText, { UDTextVariant } from 'ud-ui/components/text';

/** Скорость прокрутки по наведению, px/с: строка читается на ходу, но не убегает. */
const MARQUEE_SPEED_PX_PER_SECOND = 45;
/** Не быстрее: на коротком «хвосте» в пару пикселей прокрутка выглядела бы дёрганьем. */
const MARQUEE_MIN_DURATION_S = 1.5;

export interface UDMarqueeTextProps {
  /** Текст в одну строку. Нужна именно строка: она же уходит в `title`. */
  children: string;
  variant: UDTextVariant;
  className?: string;
}

const scroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(calc(-1 * var(--marquee-distance))); }
`;

const Viewport = styled.span`
  display: block;
  min-width: 0;
  /* Окно: обрезает бегущий текст. Многоточие — не здесь, а на самом тексте (см. ниже). */
  overflow: hidden;

  /*
   * Идёт прокрутка: у самого текста снимаются обрезка и многоточие, он становится шириной со своё содержимое и
   * едет туда и обратно с паузой на краях.
   */
  &[data-marquee='on'] > * {
    overflow: visible;
    text-overflow: clip;
    width: max-content;
    max-width: none;
    animation: ${scroll} var(--marquee-duration) linear 0.4s infinite alternate;
  }

  /* Без движения (prefers-reduced-motion) остаётся обрезка с многоточием и полный текст в подсказке title. */
  @media (prefers-reduced-motion: reduce) {
    &[data-marquee='on'] > * {
      overflow: hidden;
      text-overflow: ellipsis;
      width: auto;
      max-width: 100%;
      animation: none;
    }
  }
`;

/**
 * Строка в одну линию: не помещается — обрезается многоточием, а по наведению медленно прокручивается туда и
 * обратно, чтобы её можно было прочитать целиком. Высота блока не меняется: перенос на вторую строку, из-за
 * которого плыла высота соседей, исключён.
 *
 * Многоточие стоит на **самом тексте** (`block max-w-full overflow-hidden text-ellipsis whitespace-nowrap`), а не
 * на окне вокруг него: первый `inline-block` строки по CSS Overflow 3 обрезается, а не заменяется многоточием,
 * и длинное название кончалось бы посреди буквы без «…».
 *
 * Прокрутка включается, только если текст на самом деле шире места: короткая строка остаётся неподвижной.
 * Ширину места задаёт родитель (`min-w-0` во flex-строке). Включается по наведению мыши; с клавиатуры и касания
 * полного названия не видно — для них оно лежит в `title` и в DOM (скринридер читает его целиком), а при
 * `prefers-reduced-motion` прокрутки нет совсем.
 */
const UDMarqueeText = ({ children, variant, className }: UDMarqueeTextProps) => {
  const viewportRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  const start = useCallback(() => {
    const viewport = viewportRef.current;
    const text = textRef.current;
    if (!viewport || !text) return;

    // В покое текст ровно по ширине окна: его `scrollWidth` — длина всей строки, `clientWidth` — видимая часть.
    const distance = text.scrollWidth - text.clientWidth;
    if (distance <= 0) return;

    viewport.style.setProperty('--marquee-distance', `${distance}px`);
    viewport.style.setProperty('--marquee-duration', `${Math.max(distance / MARQUEE_SPEED_PX_PER_SECOND, MARQUEE_MIN_DURATION_S)}s`);
    viewport.dataset.marquee = 'on';
  }, []);

  const stop = useCallback(() => {
    delete viewportRef.current?.dataset.marquee;
  }, []);

  return (
    <Viewport ref={viewportRef} className={className} title={children} onMouseEnter={start} onMouseLeave={stop}>
      <UDText
        ref={textRef}
        variant={variant}
        as="span"
        className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
        data-testid="marquee-text"
      >
        {children}
      </UDText>
    </Viewport>
  );
};

export default React.memo(UDMarqueeText);
